import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { appAnalyticsService } from '../../services/appAnalyticsService';
import { CHART_COLORS } from '../../utils/chartColors';

interface TimePerModuleChartProps {
  projectId?: string;
  businessId?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

interface ModuleTimeData {
  module: string;
  totalTimeSec: number;
  avgTimeSec: number;
  count: number;
}

const TimePerModuleChart: React.FC<TimePerModuleChartProps> = ({ projectId, businessId }) => {
  const [data, setData] = useState<ModuleTimeData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId && !businessId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const result = await appAnalyticsService.getTimePerModule({ projectId: projectId || businessId });
        if (result && result.data && Array.isArray(result.data)) {
          setData(result.data.filter((d: ModuleTimeData) => d.totalTimeSec > 0));
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch time per module data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [projectId, businessId]);

  const getChartOption = () => {
    if (data.length === 0) return {};

    const sorted = [...data].sort((a, b) => b.totalTimeSec - a.totalTimeSec);
    const reversed = [...sorted].reverse();
    const names = reversed.map(d => d.module.replace(/_/g, ' '));
    const totalTimes = reversed.map(d => Math.round(d.totalTimeSec / 60 * 10) / 10); // Convert to minutes
    const avgTimes = reversed.map(d => Math.round(d.avgTimeSec * 10) / 10);

    return {
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const item = reversed[idx];
          if (!item) return '';
          return `<b>${item.module.replace(/_/g, ' ')}</b><br/>Total: ${(item.totalTimeSec / 60).toFixed(1)} min<br/>Avg per use: ${item.avgTimeSec.toFixed(1)} sec<br/>Uses: ${item.count}`;
        },
      },
      legend: {
        data: ['Total Time (min)', 'Avg per Use (sec)'],
        bottom: 0,
        textStyle: { fontFamily: FONT_FAMILY, fontSize: 10, color: '#718096' },
      },
      grid: { top: 10, right: 20, bottom: 40, left: 10, containLabel: true },
      xAxis: {
        type: 'value' as const,
        name: 'Time (minutes)',
        nameLocation: 'center' as const,
        nameGap: 25,
        nameTextStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#999' },
        axisLabel: { fontFamily: FONT_FAMILY, fontSize: 10, color: '#718096', formatter: '{value} min' },
        splitLine: { lineStyle: { color: '#EDF2F7', type: 'dashed' as const } },
      },
      yAxis: {
        type: 'category' as const,
        data: names,
        axisLabel: { fontFamily: FONT_FAMILY, fontSize: 10, color: '#4A5568', width: 100, overflow: 'truncate' as const },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          name: 'Total Time (min)',
          type: 'bar',
          stack: 'time',
          data: totalTimes.map((v, i) => ({
            value: v,
            itemStyle: { color: CHART_COLORS.palette[i % CHART_COLORS.palette.length], borderRadius: [0, 4, 4, 0] },
          })),
          barMaxWidth: 20,
          label: {
            show: true,
            position: 'right' as const,
            formatter: (p: any) => `${p.value} min`,
            fontFamily: FONT_FAMILY,
            fontSize: 9,
            color: '#718096',
          },
        },
      ],
    };
  };

  return (
    <ChartCard title="Time Spent per Module" subtitle="Total time spent on each module type" isLoading={isLoading} error={error}>
      {data.length > 0 ? (
        <ReactECharts option={getChartOption()} style={{ height: '340px', width: '100%' }} opts={{ renderer: 'svg' }} />
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            {!projectId && !businessId ? 'Select a project to view data' : 'No data available'}
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default TimePerModuleChart;
